import React, { useState, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetchArticleCommandes } from '../../../services/articleCommandeService';

ChartJS.register(LineElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const VenteParMois = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const chartRef = useRef(null);
  const [data, setData] = useState({ labels: [], datasets: [] });

  const { data: Vente, error: VenteError, isLoading: VenteLoading } = useQuery('Vente', fetchArticleCommandes);

  useEffect(() => {
    if (Vente) {
      updateChartData(Vente);
    }
  }, [Vente]);

  useEffect(() => {
    if (Vente) {
      const filtered = filterData(Vente, startDate, endDate);
      setFilteredData(filtered);
    }
  }, [startDate, endDate, Vente]);

  const extractMonthYear = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${d.getMonth() + 1}`;
  };

  const aggregateDataByMonth = (data) => {
    return data.reduce((acc, item) => {
      const monthYear = extractMonthYear(item.created_at);
      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      acc[monthYear] += item.quantite;
      return acc;
    }, {});
  };

  const updateChartData = (Vente) => {
    const VenteDataByMonth = aggregateDataByMonth(Vente);

    const months = [...new Set([...Object.keys(VenteDataByMonth)])];
    const VenteAmounts = months.map((month) => VenteDataByMonth[month] || 0);

    setData({
      labels: months,
      datasets: [
        {
          label: 'Ventes',
          data: VenteAmounts,
          borderColor: '#36A2EB',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          fill: false,
        },
      ],
    });
  };

  const filterData = (Vente, startDate, endDate) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const filteredVente = Vente.filter((vente) => {
      const venteDate = new Date(vente.created_at);
      return (!start || venteDate >= start) && (!end || venteDate <= end);
    });

    const months = [...new Set([...filteredVente.map((vente) => extractMonthYear(vente.created_at))])];

    const aggregatedVente = aggregateFilteredData(filteredVente, months);

    return months.map((month, index) => {
      const previousMonth = index > 0 ? months[index - 1] : null;
      const evolution = previousMonth ? ((aggregatedVente[month] - aggregatedVente[previousMonth]) / aggregatedVente[previousMonth]) * 100 : 0;
      return {
        mois: month,
        vente: aggregatedVente[month] || 0,
        evolution: evolution.toFixed(2),
      };
    });
  };

  const aggregateFilteredData = (data, months) => {
    return data.reduce((acc, item) => {
      const monthYear = extractMonthYear(item.created_at);
      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      acc[monthYear] += item.quantite;
      return acc;
    }, {});
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return tooltipItem.dataset.label + ': ' + tooltipItem.raw;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Mois',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Quantité',
        },
      },
    },
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facture');
    XLSX.writeFile(workbook, 'Facture.xlsx');
  };

  if (VenteLoading) return <p>Loading...</p>;
  if (VenteError) return <p>Error loading data</p>;

  return (
    <div className="container my-4">
      <div className="row mb-4 align-items-center">
        <div className="col-md-5 col-sm-12 mb-4">
          <label className="form-label">{t('Date de début')} :</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="form-control"
          />
        </div>
        <div className="col-md-5 col-sm-12 mb-4">
          <label className="form-label">{t('Date de fin')} :</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="form-control"
          />
        </div>
        <div className="col-md-2 col-sm-12 d-flex justify-content-end">
          <Link className="btn btn-primary" onClick={exportToExcel}>
            <i className="fa fa-cloud-download me-2"></i> {t('Exporter')}
          </Link>
        </div>
      </div>
      <div className="chart-container" style={{ width: '100%', height: '300px' }}>
        <Line data={data} options={options} ref={chartRef} />
      </div>
      <div className="table-responsive">
        <table className="table table-striped table-bordered w-100 mt-4">
          <thead>
            <tr>
              <th>{t('Mois')}</th>
              <th>{t('Ventes')}</th>
              <th>{t('Evolution (%)')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? filteredData.map((item, index) => (
              <tr key={index}>
                <td>{item.mois}</td>
                <td>{item.vente}</td>
                <td>
                  <span style={{ color: item.evolution > 0 ? 'green' : 'red' }}>
                    {item.evolution > 0 ? '+' : ''}{item.evolution}%
                  </span>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3">{t('Aucune donnée disponible pour la période sélectionnée')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VenteParMois;
